import Vue from 'vue';
import Vuex from 'vuex';
import * as faceapi from 'face-api.js';

Vue.use(Vuex);

/**
 * Gamification helper: XP per level.
 * Adjust to taste.
 */
const XP_PER_LEVEL = 500;

/**
 * Simple localStorage helpers (safe no-ops on SSR)
 */
const persist = {
  get(key, fallback) {
    try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; }
    catch { return fallback; }
  },
  set(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
  },
};

export default new Vuex.Store({
  state: {
    // ===== Existing app state =====
    message: '',
    labeledFaceDescriptors: null,
    firstTimeMessage: true,
     overlayOpen: false,
    overlayComponentName: null,
    overlayProps: null,
    // Face API status
    faceApi: {
      modelsLoaded: false,
      error: null,
    },

    // ===== AIDashboard state =====
    mode: persist.get('cg_mode', 'chat'),   // chat | image | video | storyboard
    listening: false,
    thinking: false,
    responding: false,

    xp: persist.get('cg_xp', 0),
    level: persist.get('cg_level', 1),
  },

  getters: {
    // progress within the current level (0..1)
    levelProgress(state) {
      const within = state.xp % XP_PER_LEVEL;
      return XP_PER_LEVEL ? within / XP_PER_LEVEL : 0;
    },
  },

  mutations: {
    // ===== Existing mutation kept =====
    updateMessage(state, payload) {
      state.message = payload.message;
    },

    // Face API mutations
    SET_FACE_DESCRIPTORS(state, desc) {
      state.labeledFaceDescriptors = desc;
    },
    SET_FACE_MODELS_LOADED(state, loaded) {
      state.faceApi.modelsLoaded = loaded;
    },
    SET_FACE_ERROR(state, msg) {
      state.faceApi.error = msg;
    },
 OPEN_OVERLAY(state, { name, props }){
      state.overlayOpen = true;
      state.overlayComponentName = name;
      state.overlayProps = props || null;
    },
    CLOSE_OVERLAY(state){
      state.overlayOpen = false;
      state.overlayComponentName = null;
      state.overlayProps = null;
    },
    // ===== AIDashboard core mutations =====
    SET_MODE(state, mode) {
      state.mode = mode;
      persist.set('cg_mode', mode);
    },
    SET_FLAGS(state, payload) {
      if (Object.prototype.hasOwnProperty.call(payload, 'listening')) state.listening = payload.listening;
      if (Object.prototype.hasOwnProperty.call(payload, 'thinking')) state.thinking = payload.thinking;
      if (Object.prototype.hasOwnProperty.call(payload, 'responding')) state.responding = payload.responding;
    },
    ADD_XP(state, amt) {
      state.xp += amt;
      // level up when passing thresholds
      while (state.xp >= state.level * XP_PER_LEVEL) state.level += 1;
      persist.set('cg_xp', state.xp);
      persist.set('cg_level', state.level);
    },
    SET_PROGRESS(state, { xp, level }) {
      if (typeof xp === 'number') state.xp = xp;
      if (typeof level === 'number') state.level = level;
      persist.set('cg_xp', state.xp);
      persist.set('cg_level', state.level);
    },
  },

  actions: {
    // ===== Face API init (async action; mutations must be sync) =====
    async initFaceAPI({ commit }) {
      try {
        const base = 'https://cogneetive-face-detection.s3.amazonaws.com/faceapi-model/models';
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(base),
          faceapi.nets.faceLandmark68Net.loadFromUri(base),
          faceapi.nets.faceRecognitionNet.loadFromUri(base),
          faceapi.nets.ssdMobilenetv1.loadFromUri(base),
        ]);
        commit('SET_FACE_MODELS_LOADED', true);
        commit('SET_FACE_ERROR', null);
      } catch (e) {
        commit('SET_FACE_MODELS_LOADED', false);
        commit('SET_FACE_ERROR', String(e && e.message ? e.message : e));
      }
    },

    // ===== AIDashboard actions =====
    startListening({ commit }) { commit('SET_FLAGS', { listening: true }); },
    stopListening({ commit })  { commit('SET_FLAGS', { listening: false }); },

    startThinking({ commit })   { commit('SET_FLAGS', { thinking: true, responding: false }); },
    startResponding({ commit }) { commit('SET_FLAGS', { thinking: false, responding: true }); },
    stopResponding({ commit })  { commit('SET_FLAGS', { responding: false, thinking: false }); },

    addXP({ commit }, amt = 25) { commit('ADD_XP', amt); },

    // Hydrate from storage if needed (optional)
    hydrateProgress({ commit }) {
      const xp = persist.get('cg_xp', 0);
      const level = persist.get('cg_level', 1);
      commit('SET_PROGRESS', { xp, level });
    },
  },

  modules: {
  },
})
